/**
 * Azure Advisor's read on the estate: what Microsoft thinks is wrong with it.
 *
 * Advisor is opinionated and noisy by design, so the panel leads with the one
 * number that is not an opinion — the annual saving Advisor has actually
 * quantified — then shape (how many, in which categories, at what impact), and
 * only then the individual items. Every number is Advisor's own; NOVA applies
 * no threshold of its own here.
 *
 * The savings total is a **floor, and says so**. Advisor prices some Cost
 * recommendations and not others, and a recommendation with no quoted amount
 * is not a recommendation worth nothing — so unpriced ones are counted in the
 * caption rather than summed in as zero, and an estate where nothing is priced
 * shows an em dash, not €0.
 */

import { Badge, DataGrid, EmptyState, SegmentBar, Statistic, Typography } from "@norican/react";

import type { AdvisorRecommendation, CloudAdvisor } from "@/lib/api";
import { advisorCategoryColor, advisorCategoryLabel } from "@/lib/charts/config";

import { PanelCard } from "./panel-card";
import { formatCount, formatMoney, IMPACT_TONE } from "./status";

interface AdvisorPanelProps {
  advisor: CloudAdvisor;
}

/** The honest caption under the savings figure — what it covers and what it misses. */
function savingsCaption(advisor: CloudAdvisor): string {
  const caveats: string[] = [];
  if (advisor.recommendations_without_savings > 0) {
    caveats.push(
      `${formatCount(advisor.recommendations_without_savings)} cost recommendation${
        advisor.recommendations_without_savings === 1 ? "" : "s"
      } quote no amount, so this is a floor`,
    );
  }
  if (advisor.savings_other_currency > 0) {
    caveats.push(
      `${formatCount(advisor.savings_other_currency)} excluded — quoted in another currency`,
    );
  }

  if (advisor.annual_savings == null) {
    return caveats.length > 0
      ? `Advisor priced none of them — ${caveats.join(" · ")}`
      : "Advisor has quantified no savings for this estate";
  }
  const priced = `From ${formatCount(advisor.recommendations_with_savings)} priced recommendation${
    advisor.recommendations_with_savings === 1 ? "" : "s"
  }`;
  return [priced, ...caveats].join(" · ");
}

export function AdvisorPanel({ advisor }: AdvisorPanelProps) {
  if (!advisor.configured) {
    return (
      <PanelCard title="Azure Advisor" caption="Not available">
        <Typography variant="body-sm" tone="muted">
          {advisor.status_detail}
        </Typography>
      </PanelCard>
    );
  }

  if (advisor.total === 0) {
    return (
      <PanelCard title="Azure Advisor" caption="Nothing outstanding">
        <EmptyState
          size="sm"
          title="Azure Advisor has no recommendations"
          description={advisor.status_detail}
        />
      </PanelCard>
    );
  }

  const segments = advisor.by_category.map((category) => ({
    value: category.total,
    label: `${advisorCategoryLabel(category.category)} (${category.total})`,
    color: advisorCategoryColor(category.category),
  }));

  return (
    <PanelCard
      title="Azure Advisor"
      caption={`${formatCount(advisor.total)} recommendations across ${advisor.by_subscription.length} subscriptions`}
    >
      <div className="flex flex-col gap-5">
        <Statistic
          label="Advisor says we're leaving this on the table (annual)"
          value={formatMoney(advisor.annual_savings, advisor.savings_currency)}
          tone={advisor.annual_savings == null ? "neutral" : "accent"}
          size="lg"
          caption={savingsCaption(advisor)}
        />

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Statistic
            label="High impact"
            value={formatCount(advisor.high)}
            tone="danger"
            caption="Advisor's own rating"
          />
          <Statistic label="Medium impact" value={formatCount(advisor.medium)} tone="warning" />
          <Statistic label="Low impact" value={formatCount(advisor.low)} tone="neutral" />
          <Statistic
            label="Worst subscription"
            value={advisor.by_subscription[0]?.name ?? "—"}
            caption={
              advisor.by_subscription[0]
                ? `${advisor.by_subscription[0].total} recommendations · ${advisor.by_subscription[0].high} high`
                : undefined
            }
          />
        </div>

        <div className="flex flex-col gap-2">
          <Typography variant="label" as="h3">
            By category
          </Typography>
          <SegmentBar
            segments={segments}
            size="lg"
            showLegend
            aria-label="Advisor recommendations by category"
          />
        </div>

        {advisor.top_savings.length > 0 && (
          <div className="flex flex-col gap-2">
            <Typography variant="label" as="h3">
              Biggest savings first
            </Typography>
            <DataGrid<AdvisorRecommendation>
              data={advisor.top_savings}
              getRowId={(item) => item.id}
              density="compact"
              aria-label="Azure Advisor recommendations with the largest quantified savings"
              columns={[
                {
                  id: "saving",
                  header: "Per year",
                  accessor: (item) => item.annual_savings,
                  sortable: true,
                  align: "end",
                  cell: (item) => (
                    <span className="text-fg font-medium tabular-nums">
                      {formatMoney(item.annual_savings, item.savings_currency)}
                    </span>
                  ),
                },
                {
                  id: "problem",
                  header: "Recommendation",
                  accessor: (item) => item.problem,
                  sortable: true,
                  cell: (item) => <span className="text-fg">{item.problem}</span>,
                },
                {
                  id: "resource",
                  header: "Resource",
                  accessor: (item) => item.impacted_value ?? "",
                  sortable: true,
                  cell: (item) => (
                    <div className="flex flex-col">
                      <span className="text-fg text-sm">{item.impacted_value ?? "—"}</span>
                      <span className="text-fg-muted text-xs">{item.subscription_name}</span>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Typography variant="label" as="h3">
            Highest impact first
          </Typography>
          <DataGrid<AdvisorRecommendation>
            data={advisor.top}
            getRowId={(item) => item.id}
            density="compact"
            aria-label="Top Azure Advisor recommendations"
            columns={[
              {
                id: "impact",
                header: "Impact",
                accessor: (item) => item.impact,
                sortable: true,
                cell: (item) => (
                  <Badge tone={IMPACT_TONE[item.impact] ?? "neutral"} variant="subtle" size="xs">
                    {item.impact}
                  </Badge>
                ),
              },
              {
                id: "problem",
                header: "Recommendation",
                accessor: (item) => item.problem,
                sortable: true,
                cell: (item) => (
                  <div className="flex flex-col">
                    <span className="text-fg">{item.problem}</span>
                    {item.solution && item.solution !== item.problem && (
                      <span className="text-fg-muted text-xs">{item.solution}</span>
                    )}
                  </div>
                ),
              },
              {
                id: "category",
                header: "Category",
                accessor: (item) => item.category,
                sortable: true,
                cell: (item) => (
                  <span className="text-fg-muted text-sm">
                    {advisorCategoryLabel(item.category)}
                  </span>
                ),
              },
              {
                id: "saving",
                header: "Per year",
                accessor: (item) => item.annual_savings,
                sortable: true,
                align: "end",
                cell: (item) => (
                  <span className="text-fg-muted text-sm tabular-nums">
                    {formatMoney(item.annual_savings, item.savings_currency)}
                  </span>
                ),
              },
              {
                id: "resource",
                header: "Resource",
                accessor: (item) => item.impacted_value ?? "",
                sortable: true,
                cell: (item) => (
                  <div className="flex flex-col">
                    <span className="text-fg text-sm">{item.impacted_value ?? "—"}</span>
                    <span className="text-fg-muted text-xs">{item.subscription_name}</span>
                  </div>
                ),
              },
            ]}
          />
        </div>

        {advisor.subscriptions_failed.length > 0 && (
          <Typography variant="body-sm" tone="muted">
            Advisor could not be read for{" "}
            {advisor.subscriptions_failed.map((problem) => problem.name).join(", ")}, so those
            subscriptions are not counted above.
          </Typography>
        )}
      </div>
    </PanelCard>
  );
}
